import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import PromoBanner from "@/components/PromoBanner";
import Home from "@/pages/Home";
import AppDetail from "@/pages/AppDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Checkout from "@/pages/Checkout";
import Admin from "@/pages/Admin";
import AdminAppForm from "@/pages/AdminAppForm";
import AuthCallback from "@/pages/AuthCallback";
import Trial from "@/pages/Trial";

const LOCAL_ORDERS_KEY = "weborder.localOrders";
const ORDER_HISTORY_CLEAN_KEY = "weborder.orderHistoryCleaned.v1";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AppShell() {
  const location = useLocation();
  // Synchronous check for OAuth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <PromoBanner />
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/apps" element={<Navigate to="/" replace />} />
        <Route path="/mobile" element={<Navigate to="/" replace />} />
        <Route path="/android" element={<Navigate to="/try/android" replace />} />
        <Route path="/ios" element={<Navigate to="/try/ios" replace />} />
        <Route path="/apps/:id" element={<AppDetail />} />
        <Route path="/try/:edition" element={<Trial />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/checkout/:id" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
        <Route path="/admin/apps/new" element={<ProtectedRoute adminOnly><AdminAppForm /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="top-right" richColors />
    </>
  );
}

export default function App() {
  useEffect(() => {
    if (!localStorage.getItem(ORDER_HISTORY_CLEAN_KEY)) {
      localStorage.removeItem(LOCAL_ORDERS_KEY);
      localStorage.setItem(ORDER_HISTORY_CLEAN_KEY, "true");
    }
  }, []);

  return (
    <div className="App min-h-screen text-[#0F0F1B]">
      <BrowserRouter>
        <AuthProvider>
          <AppShell />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
